/**
 * TenantFeatureManager Component
 * 
 * Allows Platform Admins to manage which features are enabled for a tenant.
 */

import React, { useState, useEffect } from 'react';
import { Tenant, TenantFeature } from '../../types/admin';
import {
  updateTenantFeatures,
  AVAILABLE_FEATURES,
  getFeatureInfo,
} from '../../services/featureService';

interface TenantFeatureManagerProps {
  tenant: Tenant;
  onUpdate?: (updatedTenant: Tenant) => void;
}

export function TenantFeatureManager({
  tenant,
  onUpdate,
}: TenantFeatureManagerProps) {
  // Ensure enabledFeatures is always an array
  const initialFeatures = Array.isArray(tenant.enabledFeatures) 
    ? tenant.enabledFeatures 
    : [];
    
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(initialFeatures);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('TenantFeatureManager - tenant:', tenant);
    console.log('TenantFeatureManager - tenant.enabledFeatures:', tenant.enabledFeatures);
    console.log('TenantFeatureManager - typeof tenant.enabledFeatures:', typeof tenant.enabledFeatures);
    console.log('TenantFeatureManager - Array.isArray(tenant.enabledFeatures):', Array.isArray(tenant.enabledFeatures));
    console.log('TenantFeatureManager - selectedFeatures:', selectedFeatures);
    console.log('TenantFeatureManager - AVAILABLE_FEATURES:', AVAILABLE_FEATURES);
  }, [tenant, selectedFeatures]);

  // Update selected features when tenant changes
  useEffect(() => {
    const features = Array.isArray(tenant.enabledFeatures) 
      ? tenant.enabledFeatures 
      : [];
    console.log('Setting selectedFeatures to:', features);
    setSelectedFeatures(features);
  }, [tenant, tenant.enabledFeatures]);

  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures((prev) => {
      if (prev.includes(feature)) {
        return prev.filter((f) => f !== feature);
      } else {
        return [...prev, feature];
      }
    });
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      console.log('Saving features:', {
        tenantId: tenant.tenantId,
        selectedFeatures
      });

      const updatedTenant = await updateTenantFeatures(
        tenant.tenantId,
        selectedFeatures
      );

      console.log('Updated tenant:', updatedTenant);

      setSuccess(true);
      if (onUpdate) {
        onUpdate(updatedTenant);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update features:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to update features'
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    JSON.stringify([...selectedFeatures].sort()) !==
    JSON.stringify([...(tenant.enabledFeatures || [])].sort());

  return (
    <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
      <h3 style={{ marginBottom: '1rem' }}>Feature Access</h3>

      <div style={{ marginBottom: '1rem' }}>
        {AVAILABLE_FEATURES.map((feature) => {
          const isSelected = selectedFeatures.includes(feature.key);
          console.log(`Feature ${feature.key}: isSelected=${isSelected}, selectedFeatures=`, selectedFeatures);
          return (
            <div
              key={feature.key}
              style={{
                padding: '0.75rem',
                marginBottom: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: isSelected ? '#e8f5e9' : '#fff',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleFeatureToggle(feature.key)}
                  style={{ marginRight: '0.75rem', marginTop: '0.25rem' }}
                />
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    {feature.name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>
                    {feature.description}
                  </div>
                </div>
              </label>
            </div>
          );
        })}
      </div>

      {error && (
        <div
          style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            borderRadius: '4px',
          }}
        >
          Features updated successfully!
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: hasChanges ? '#1976d2' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: hasChanges ? 'pointer' : 'not-allowed',
            fontWeight: 'bold',
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        {hasChanges && (
          <button
            onClick={() => setSelectedFeatures(tenant.enabledFeatures || [])}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#fff',
              color: '#666',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
        <strong>Currently Enabled:</strong>{' '}
        {selectedFeatures.length > 0
          ? selectedFeatures
              .map((f) => getFeatureInfo(f)?.name || f)
              .join(', ')
          : 'No features enabled'}
      </div>
    </div>
  );
}
